import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../atoms/Input';
import { PATHS } from '../commons/paths';
import { VisibilityButton } from '../atoms/VisibilityButton';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { FormSectionTitle } from 'atoms/FormSectionTitle';
import { Form } from 'templates/Form';
import { PageContent } from 'templates/PageContent';
import { UnauthHeader } from 'templates/UnauthHeader';
import { useCreateConfirmResetPasswordSchema } from 'hooks/u007/useCreateConfirmResetPasswordSchema';
import { CreateConfirmResetPasswordType } from 'commons/passwordChange';
import { FieldErrorMessages } from 'molecules/FieldErrorMessages';
import { BackButton } from 'atoms/BackButton';
import { Button } from 'atoms/Button';
import MessageModal from 'organisms/MessageModal';
import LoadingModal from 'organisms/LoadingModal';
import { useConfirmResetPassword } from 'hooks/u007/useConfirmResetPassword';
import { UnauthAppFooter } from 'templates/UnauthAppFooter';
import { RequiredLabel } from 'molecules/RequiredLabel';
import { AppContext } from 'templates/AppContextProvider';

export function ConfirmResetPassword() {
  // hooks
  const navigate = useNavigate();
  const { t: tCommon } = useTranslationWithLang('commons');
  const ResetPasswordSchema = useCreateConfirmResetPasswordSchema();
  const [confirmResetPassword] = useConfirmResetPassword();
  const { currentLang } = useContext(AppContext);
  // state
  const [isShowNewPassword, setIsShowNewPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);
  const [infoModal, setInfoModal] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState('');
  // form
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<CreateConfirmResetPasswordType>({
    resolver: zodResolver(ResetPasswordSchema),
    shouldFocusError: true,
  });

  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);

  // function
  const onClickNewPasswordVisibilityIcon = useCallback(
    () => setIsShowNewPassword((isShowNewPassword) => !isShowNewPassword),
    [isShowNewPassword]
  );
  const onClickConfirmPasswordVisibilityIcon = useCallback(
    () => setIsShowConfirmPassword((isShowConfirmPassword) => !isShowConfirmPassword),
    [isShowConfirmPassword]
  );

  const onSubmit: SubmitHandler<CreateConfirmResetPasswordType> = async (form) => {
    setLoading(true);
    confirmResetPassword({
      request: {
        username: form.id,
        verification_code: form.code,
        new_password: form.password,
      },
      onSuccess: () => {
        setLoading(false);
        setInfoModal(tCommon('TU000_044'));
      },
      onError: (err) => {
        setLoading(false);
        setErrorModal(
          err.message === 'MU007F0116E'
            ? tCommon('TU000_035')
            : err.message === 'MU007F0117E'
              ? tCommon('TU000_037')
              : tCommon('TU000_045')
        );
      },
    });
  };

  // value
  const newPasswordInputType = useMemo(
    () => (isShowNewPassword ? 'text' : 'password'),
    [isShowNewPassword]
  );
  const confirmPasswordInputType = useMemo(
    () => (isShowConfirmPassword ? 'text' : 'password'),
    [isShowConfirmPassword]
  );

  return (
    <>
      <UnauthHeader />
      {loading && <LoadingModal />}
      {infoModal && (
        <MessageModal
          message={infoModal}
          closeLabel={tCommon('TU000_055')}
          onClose={() => navigate(PATHS.LOGIN)}
        />
      )}
      {errorModal && (
        <MessageModal
          message={errorModal}
          closeLabel={tCommon('TU000_028')}
          onClose={() => setErrorModal('')}
        />
      )}
      <PageContent>
        <div role="heading" aria-level={1} aria-live="assertive" className="sr-only">
          {tCommon('TU000_015')}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form title={tCommon('TU000_015')}>
            <div className="flex flex-col w-full">
              <div className="flex items-center gap-2">
                <FormSectionTitle text={tCommon('TU000_016')} htmlFor="id-input" />
                <RequiredLabel id="id-input" />
              </div>
              <div className="pt-2">
                <Input
                  id="id-input"
                  type="text"
                  className="px-5 rounded-lg mt-2"
                  {...register('id')}
                  aria-label={tCommon('TU000_016')}
                  autoComplete="username"
                />
                <FieldErrorMessages messages={[errors.id?.message]} />
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-2">
                  <FormSectionTitle text={tCommon('TU000_017')} htmlFor="code-input" />
                  <RequiredLabel id="code-input" />
                </div>
                <Input
                  id="code-input"
                  type="text"
                  className="px-5 rounded-lg mt-2"
                  {...register('code')}
                  aria-label={tCommon('TU000_017')}
                />
                <FieldErrorMessages messages={[errors.code?.message]} />
              </div>

              <div className="relative mt-6">
                <div className="flex items-center gap-2">
                  <FormSectionTitle text={tCommon('TU000_019')} htmlFor="password-input" />
                  <RequiredLabel id="password-input" />
                </div>
                <div className="pt-2">
                  <Input
                    id="password-input"
                    type={newPasswordInputType}
                    className="pr-10 px-5 rounded-lg"
                    {...register('password')}
                    aria-label={tCommon('TU000_019')}
                    autoComplete="new-password"
                  />
                  <VisibilityButton
                    className="absolute top-12 right-4 cursor-pointer"
                    isShow={isShowNewPassword}
                    onClick={onClickNewPasswordVisibilityIcon}
                  />
                  <p className="text-sm text-[--c-text2] mb-2">{tCommon('TU000_080')}</p>
                  <FieldErrorMessages messages={[errors.password?.message]} />
                </div>
              </div>

              <div className="relative mt-6">
                <div className="flex items-center gap-2">
                  <FormSectionTitle
                    text={tCommon('TU000_020')}
                    htmlFor="password-confirmation-input"
                  />
                  <RequiredLabel id="password-confirmation-input" />
                </div>
                <div className="pt-2">
                  <Input
                    id="password-confirmation-input"
                    type={confirmPasswordInputType}
                    className="pr-10 px-5 rounded-lg"
                    {...register('passwordConfirmation')}
                    aria-label={tCommon('TU000_020')}
                    autoComplete="new-password"
                  />
                  <VisibilityButton
                    className="absolute top-12 right-4 cursor-pointer"
                    isShow={isShowConfirmPassword}
                    onClick={onClickConfirmPasswordVisibilityIcon}
                  />
                  <FieldErrorMessages messages={[errors.passwordConfirmation?.message]} />
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center h-10 my-10 mt-5">
              <BackButton className="mr-2 w-[9rem]" />
              <Button type="submit" label={tCommon('TU000_048')} className="w-[9rem]" />
            </div>
          </Form>
        </form>
      </PageContent>
      <UnauthAppFooter />
    </>
  );
}
